/** Committee term countdown banner + Issue iCard button + Preview
 *  mockups link. Extracted from legacy TradeOrgDashboard.jsx. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Plus, Sparkles, Copy, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function TermBanner({ org, onChange, accent = "#F5B82E" }) {
  const [icardOpen, setIcardOpen]     = useState(false);
  const [issuedLink, setIssuedLink]   = useState("");
  const [dialogKey, setDialogKey]     = useState(0);

  const termEnds    = org.committee_term_ends_at;
  const termExpired = termEnds && new Date(termEnds) < new Date();

  const handleOpenChange = (v) => {
    setIcardOpen(v);
    if (!v) {
      setIssuedLink("");
      setDialogKey((k) => k + 1);
    }
  };

  return (
    <div
      className={`mt-4 cs-card rounded-2xl p-4 flex items-center gap-3 ${
        termExpired ? "border border-red-500/40" : ""
      }`}
      data-testid="term-banner"
    >
      <Clock size={18} className={termExpired ? "text-red-300" : "text-white/55"} />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white/55 tracking-[0.18em] uppercase">Committee Term</div>
        <div className="text-sm mt-0.5">
          {termEnds ? (
            termExpired ? (
              <span className="text-red-300 font-semibold">Expired — fresh election required</span>
            ) : (
              <>
                Ends {new Date(termEnds).toLocaleDateString()}{" "}
                <span className="text-white/40">· {org.committee_term_years} year term</span>
              </>
            )
          ) : (
            <span className="text-white/40">No term set</span>
          )}
        </div>
      </div>
      <Dialog open={icardOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em]"
            style={{ background: accent, color: "#0b0b0d" }}
            data-testid="issue-icard-btn"
          >
            <Plus size={12} className="mr-1.5" /> Issue iCard
          </Button>
        </DialogTrigger>
        <IssueIcardDialog
          key={dialogKey}
          orgId={org.id}
          issuedLink={issuedLink}
          setIssuedLink={setIssuedLink}
          onOpenChange={handleOpenChange}
          onDone={onChange}
        />
      </Dialog>
      <Link
        to="/icard/mockups"
        data-testid="preview-mockups-link"
        className="h-9 inline-flex items-center px-3 rounded-full text-[10px] uppercase tracking-[0.22em] border border-white/15 text-white/75 hover:border-white/35 hover:text-white"
      >
        <Sparkles size={12} className="mr-1.5" style={{ color: accent }} />
        Preview iCard styles
      </Link>
    </div>
  );
}

function IssueIcardDialog({ orgId, issuedLink, setIssuedLink, onOpenChange, onDone }) {
  const [name, setName]   = useState("");
  const [phone, setPhone] = useState("");
  const [memNo, setMemNo] = useState("");
  const [role, setRole]   = useState("production");
  const [city, setCity]   = useState("Mumbai");
  const [busy, setBusy]   = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${orgId}/icard-invites`, {
        invitee_name: name, invitee_phone: phone, membership_number: memNo,
        primary_role: role, city,
      });
      const url = data.invite_url?.startsWith("http")
        ? data.invite_url : `${window.location.origin}${data.invite_url}`;
      setIssuedLink(url);
      toast.success("iCard issued — share the link with the member");
      await onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not issue");
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(issuedLink).then(() => {
      setCopied(true); toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      console.warn("[IssueIcardDialog] clipboard write failed:", err?.message);
      toast.error("Could not copy — please copy manually");
    });
  };

  if (issuedLink) {
    return (
      <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="icard-issued-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">iCard Ready</DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Share this link with your member. They open it on their phone, set a password and the iCard lands fully populated.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-[11px] font-mono text-white/70 break-all"
               data-testid="icard-issued-link">{issuedLink}</div>
          <Button onClick={copy} className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                  data-testid="copy-icard-link">
            {copied ? <Check size={13} className="mr-2" /> : <Copy size={13} className="mr-2" />}
            {copied ? "Copied" : "Copy Link"}
          </Button>
          <Button onClick={() => { setIssuedLink(""); onOpenChange(false); }}
                  className="w-full h-10 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/10 hover:bg-white/15 text-white">
            Done
          </Button>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="issue-icard-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Issue Member iCard</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          Generate a claim link. Sent via SMS/email in v2; copy-paste for now.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Full Name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-2 h-10 bg-black/40 border-white/15" data-testid="icard-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Phone *</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 …"
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="icard-phone-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Membership # *</Label>
            <Input value={memNo} onChange={(e) => setMemNo(e.target.value)} placeholder="IFTDA/D/2024/00501"
                   className="mt-2 h-10 bg-black/40 border-white/15 font-mono uppercase"
                   data-testid="icard-memno-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Primary Role</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)}
                   placeholder="director / dop / editor / production"
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="icard-role-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)}
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="icard-city-input" />
          </div>
        </div>
        <Button onClick={submit} disabled={!name || !phone || !memNo || busy}
                className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                data-testid="icard-submit">
          {busy ? "Issuing…" : "Issue iCard"}
        </Button>
      </div>
    </DialogContent>
  );
}
